import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FolderOpen, Menu, Search } from "lucide-react";

const MACRO_OPTIONS = [
  { value: "Prodotti Triolo", label: "Prodotti Triolo", color: "text-amber-400 bg-amber-500/10" },
  { value: "Salumi", label: "Salumi", color: "text-red-400 bg-red-500/10" },
  { value: "Formaggi", label: "Formaggi", color: "text-yellow-400 bg-yellow-500/10" },
  { value: "Olive, Sottoli, Pesti", label: "Olive, Sottoli, Pesti", color: "text-emerald-400 bg-emerald-500/10" },
  { value: "Prodotti Ittici", label: "Prodotti Ittici", color: "text-cyan-400 bg-cyan-500/10" },
  { value: "Frutta Secca e Aromi", label: "Frutta Secca e Aromi", color: "text-orange-400 bg-orange-500/10" },
  { value: "Altri", label: "Altri", color: "text-zinc-400 bg-zinc-500/10" },
];

const MacroBadge = ({ macro }) => {
  const opt = MACRO_OPTIONS.find(m => m.value === macro) || MACRO_OPTIONS[6];
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${opt.color}`}>{opt.label}</span>;
};

export default function AdminCategories() {
  const { authAxios } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMacro, setFilterMacro] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image_url: "",
  });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await authAxios.get("/categories");
      setCategories(res.data);
    } catch {
      toast.error("Errore nel caricamento delle categorie");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const resetForm = () => {
    setFormData({ name: "", description: "", image_url: "" });
    setEditingCategory(null);
  };

  const openCreateDialog = () => { resetForm(); setDialogOpen(true); };
  const openEditDialog = (cat) => {
    setEditingCategory(cat);
    setFormData({ name: cat.name, description: cat.description || "", image_url: cat.image_url || "" });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await authAxios.put(`/categories/${editingCategory.id}`, formData);
        toast.success("Categoria aggiornata");
      } else {
        await authAxios.post("/categories", formData);
        toast.success("Categoria creata");
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore");
    }
  };

  const handleDelete = async () => {
    try {
      await authAxios.delete(`/categories/${categoryToDelete.id}`);
      toast.success("Categoria eliminata");
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const quickChangeMacro = async (categoryId, newMacro) => {
    try {
      await authAxios.put(`/categories/${categoryId}/macro`, { macro_category: newMacro });
      setCategories(prev => prev.map(c =>
        c.id === categoryId ? { ...c, macro_category: newMacro, is_triolo: newMacro === "Prodotti Triolo" } : c
      ));
      toast.success("Sezione catalogo aggiornata");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const filtered = categories.filter((c) => {
    if (c.product_count === 0) return false;
    const matchSearch = !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || (c.code || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchMacro = filterMacro === "all" || c.macro_category === filterMacro;
    return matchSearch && matchMacro;
  }).sort((a, b) => (a.code || "").localeCompare(b.code || ""));

  // Only count categories with products
  const activeCategories = categories.filter(c => c.product_count > 0);
  const macroCounts = {};
  activeCategories.forEach(c => {
    const m = c.macro_category || "Altri";
    macroCounts[m] = (macroCounts[m] || 0) + 1;
  });

  return (
    <div className="min-h-screen bg-[#09090b]">

      <main className="p-4 sm:p-6 lg:p-8">
        <PageHeader title="Categorie" />
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-1">Categorie</h1>
              <p className="text-zinc-400 text-sm sm:text-base">Organizza le categorie nelle sezioni del catalogo</p>
            </div>
          </div>
          <Button data-testid="add-category-btn" onClick={openCreateDialog} className="bg-amber-500 hover:bg-amber-600 text-black">
            <Plus className="w-4 h-4 mr-2" /> Nuova Categoria
          </Button>
        </div>

        {/* Macro summary pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilterMacro("all")}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filterMacro === "all" ? "bg-white/10 text-white" : "bg-white/5 text-zinc-500 hover:text-zinc-300"}`}
          >
            Tutte ({activeCategories.length})
          </button>
          {MACRO_OPTIONS.map(opt => {
            const count = macroCounts[opt.value] || 0;
            if (count === 0) return null;
            return (
              <button
                key={opt.value}
                onClick={() => setFilterMacro(filterMacro === opt.value ? "all" : opt.value)}
                className={`text-xs px-3 py-1.5 rounded-full transition-colors ${filterMacro === opt.value ? opt.color + " ring-1 ring-current" : "bg-white/5 text-zinc-500 hover:text-zinc-300"}`}
              >
                {opt.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <Input
              data-testid="categories-search"
              type="text"
              placeholder="Cerca categoria..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#18181b] border-white/10"
            />
          </div>
        </div>

        {/* Categories Table */}
        <div className="bg-[#18181b] rounded-xl border border-white/5 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center">
              <FolderOpen className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400">Nessuna categoria trovata</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Codice</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Categoria</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider">Sezione Catalogo</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-center">Attuale</TableHead>
                  <TableHead className="text-zinc-400 uppercase text-xs tracking-wider text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((cat) => (
                  <TableRow key={cat.id} data-testid={`cat-row-${cat.id}`} className="border-white/5">
                    <TableCell>
                      <span className="font-mono text-amber-400 text-xs">{cat.code}</span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-white">{cat.name}</span>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={cat.macro_category || "Altri"}
                        onValueChange={(val) => quickChangeMacro(cat.id, val)}
                      >
                        <SelectTrigger
                          data-testid={`macro-select-${cat.id}`}
                          className="h-8 w-[220px] bg-transparent border-white/5 hover:border-amber-500/30 text-xs"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#27272a] border-white/10">
                          {MACRO_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-xs">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      <MacroBadge macro={cat.macro_category || "Altri"} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" data-testid={`edit-category-${cat.id}`} onClick={() => openEditDialog(cat)} className="text-zinc-400 hover:text-white">
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-category-${cat.id}`} onClick={() => { setCategoryToDelete(cat); setDeleteDialogOpen(true); }} className="text-zinc-400 hover:text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">
                {editingCategory ? "Modifica Categoria" : "Nuova Categoria"}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {editingCategory ? "Modifica i dettagli della categoria" : "Inserisci i dettagli della nuova categoria"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Nome *</Label>
                <Input
                  data-testid="category-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-[#27272a] border-white/10"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Descrizione</Label>
                <Textarea
                  data-testid="category-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#27272a] border-white/10"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Annulla</Button>
                <Button type="submit" data-testid="category-submit-btn" className="bg-amber-500 hover:bg-amber-600 text-black">
                  {editingCategory ? "Salva" : "Crea"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-[#18181b] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Conferma Eliminazione</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Sei sicuro di voler eliminare "{categoryToDelete?.name}"?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-white/10">Annulla</Button>
              <Button data-testid="confirm-delete-category-btn" onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Elimina</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
